import { useState } from "react";
import { Knopf } from "../Knopf";
import { Markierung } from "./Markierung";
import { ZeichenSchloss } from "./Zeichen";
import { euro } from "./texte";
import type { OffeneFreigabe } from "./typen";

/**
 * The approval, in the transcript.
 *
 * The four-eyes principle rendered as UI. It is deliberately not a toast and not a
 * modal: a toast can be missed and a modal detaches the decision from the work that
 * produced it. Here the request sits exactly where the run stopped, with the steps
 * that led to it still on screen above, and the run resumes in the same place.
 *
 * What the signer reads is what the signature covers. The card shows the kernel's own
 * summary and, where something changes, the old value beside the new one — an old IBAN
 * next to a new IBAN is where invoice fraud is actually caught. Nothing here is
 * abbreviated for layout.
 */

export type Entscheidung = "erteilt" | "verweigert";

export interface FreigabekarteProps {
  titel: string;
  daten: OffeneFreigabe | null;
  /** Set once this request has been decided in this session. */
  entschieden: Entscheidung | null;
  /** The run has moved past this checkpoint — it was decided, possibly in another tab. */
  erledigt: boolean;
  fehler: string | null;
  laeuft: boolean;
  onEntscheiden: (entscheidung: Entscheidung, notiz: string | null) => void;
}

export function Freigabekarte({
  titel,
  daten,
  entschieden,
  erledigt,
  fehler,
  laeuft,
  onEntscheiden,
}: FreigabekarteProps) {
  const [notiz, setNotiz] = useState("");
  const vierAugen = daten?.art === "vier_augen";

  return (
    <section
      className="border border-l-2 px-[8px] py-[8px] flex flex-col gap-[6px] rounded-[var(--radius-2)]"
      style={{
        borderColor: "var(--line-strong)",
        borderLeftColor: vierAugen ? "var(--status-bad-bar)" : "var(--status-warn-bar)",
        background: "var(--surface-raised)",
      }}
      aria-label="Freigabe erforderlich"
    >
      <header className="flex items-center gap-[4px]">
        <span style={{ color: vierAugen ? "var(--status-bad-tx)" : "var(--status-warn-tx)" }}>
          <ZeichenSchloss size={13} />
        </span>
        <span
          className="text-[length:var(--text-2xs)]"
          style={{
            color: vierAugen ? "var(--status-bad-tx)" : "var(--status-warn-tx)",
            textTransform: "var(--label-transform)" as never,
            letterSpacing: "var(--tracking-caps)",
            fontWeight: "var(--weight-medium)",
          }}
        >
          {vierAugen ? "Vier-Augen-Freigabe" : "Freigabe erforderlich"}
        </span>
        {daten && daten.benoetigteZeichnungen > 1 ? (
          <span className="num ml-auto text-[length:var(--text-3xs)]" style={{ color: "var(--text-muted)" }}>
            {daten.geleisteteZeichnungen} von {daten.benoetigteZeichnungen} Zeichnungen
          </span>
        ) : null}
      </header>

      <p
        className="text-[length:var(--text-sm)]"
        style={{ color: "var(--text-primary)", fontWeight: "var(--weight-medium)" }}
      >
        {daten?.titel ?? titel}
      </p>

      {daten?.zusammenfassungMd ? <Markierung text={daten.zusammenfassungMd} /> : null}

      {typeof daten?.betragCent === "number" ? (
        <p className="flex items-baseline gap-[6px]">
          <span className="text-[length:var(--text-2xs)]" style={{ color: "var(--text-caps)" }}>
            Betrag
          </span>
          <span
            className="num text-[length:var(--text-base)]"
            style={{ color: "var(--text-primary)", fontWeight: "var(--weight-medium)" }}
          >
            {euro(daten.betragCent)}
          </span>
        </p>
      ) : null}

      {daten ? <Unterschied diff={daten.diff} /> : null}

      {entschieden ? (
        <p
          className="text-[length:var(--text-xs)]"
          style={{
            color: entschieden === "erteilt" ? "var(--status-ok-tx)" : "var(--text-secondary)",
          }}
        >
          {entschieden === "erteilt"
            ? "Freigegeben. Der Lauf wurde fortgesetzt."
            : "Abgelehnt. Der Assistent wurde darüber unterrichtet."}
        </p>
      ) : erledigt ? (
        <p className="text-[length:var(--text-xs)]" style={{ color: "var(--text-secondary)" }}>
          Über diese Freigabe wurde bereits entschieden. Die Entscheidung steht im
          Freigabeprotokoll.
        </p>
      ) : (
        <>
          <div className="flex flex-col gap-[2px]">
            <label
              htmlFor={`notiz-${daten?.id ?? "freigabe"}`}
              className="text-[length:var(--text-3xs)]"
              style={{
                color: "var(--text-caps)",
                textTransform: "var(--label-transform)" as never,
                letterSpacing: "var(--tracking-caps)",
              }}
            >
              Vermerk (freiwillig)
            </label>
            <input
              id={`notiz-${daten?.id ?? "freigabe"}`}
              value={notiz}
              onChange={(e) => setNotiz(e.target.value)}
              className="h-[24px] border px-[6px] text-[length:var(--text-xs)] rounded-[var(--radius-2)]"
              style={{
                background: "var(--surface-field)",
                borderColor: "var(--line-field)",
                color: "var(--text-primary)",
              }}
            />
          </div>

          <div className="flex items-center gap-[6px]">
            <Knopf
              variante="primaer"
              groesse="klein"
              laeuft={laeuft}
              onClick={() => onEntscheiden("erteilt", notiz.trim() || null)}
            >
              Freigeben
            </Knopf>
            <Knopf
              variante="gefahr"
              groesse="klein"
              disabled={laeuft}
              onClick={() => onEntscheiden("verweigert", notiz.trim() || null)}
            >
              Ablehnen
            </Knopf>
          </div>
        </>
      )}

      {fehler ? (
        <p className="text-[length:var(--text-xs)]" style={{ color: "var(--status-bad-tx)" }}>
          {fehler}
        </p>
      ) : null}
    </section>
  );
}

/**
 * Before and after.
 *
 * Two labelled columns rather than one line with a symbol between them: at this width
 * a symbol wraps, and a wrapped diff is a diff nobody checks.
 */
function Unterschied({ diff }: { diff: Record<string, unknown> }) {
  const felder = Object.entries(diff ?? {});
  if (felder.length === 0) return null;

  return (
    <table className="w-full border-collapse text-[length:var(--text-xs)]">
      <thead>
        <tr>
          <th
            className="text-left px-[4px] py-[2px] border-b font-[var(--weight-medium)]"
            style={{ color: "var(--text-caps)", borderColor: "var(--line-hairline)" }}
          >
            Feld
          </th>
          <th
            className="text-left px-[4px] py-[2px] border-b font-[var(--weight-medium)]"
            style={{ color: "var(--text-caps)", borderColor: "var(--line-hairline)" }}
          >
            Bisher
          </th>
          <th
            className="text-left px-[4px] py-[2px] border-b font-[var(--weight-medium)]"
            style={{ color: "var(--text-caps)", borderColor: "var(--line-hairline)" }}
          >
            Neu
          </th>
        </tr>
      </thead>
      <tbody>
        {felder.map(([feld, wert]) => {
          const paar = alsPaar(wert);
          return (
            <tr key={feld}>
              <td
                className="px-[4px] py-[2px] align-top border-b"
                style={{ color: "var(--text-secondary)", borderColor: "var(--line-hairline)" }}
              >
                {feld}
              </td>
              <td
                className="mono px-[4px] py-[2px] align-top border-b break-words"
                style={{ color: "var(--text-muted)", borderColor: "var(--line-hairline)" }}
              >
                {paar.alt}
              </td>
              <td
                className="mono px-[4px] py-[2px] align-top border-b break-words"
                style={{
                  color: "var(--text-primary)",
                  borderColor: "var(--line-hairline)",
                  fontWeight: "var(--weight-medium)",
                }}
              >
                {paar.neu}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** Accepts `{alt, neu}`, `{vorher, nachher}` or a bare value that is only the new one. */
function alsPaar(wert: unknown): { alt: string; neu: string } {
  if (wert && typeof wert === "object" && !Array.isArray(wert)) {
    const objekt = wert as Record<string, unknown>;
    const alt = objekt.alt ?? objekt.vorher ?? objekt.bisher;
    const neu = objekt.neu ?? objekt.nachher ?? objekt.wert;
    if (alt !== undefined || neu !== undefined) {
      return { alt: alsText(alt), neu: alsText(neu) };
    }
  }
  return { alt: "—", neu: alsText(wert) };
}

function alsText(wert: unknown): string {
  if (wert === null || wert === undefined) return "—";
  if (typeof wert === "string") return wert;
  if (typeof wert === "number" || typeof wert === "boolean") return String(wert);
  try {
    return JSON.stringify(wert);
  } catch {
    return String(wert);
  }
}
